import React, { useState, useRef, useEffect } from "react";
import { setCookie, removeCookie } from "../ReusableFunctions/cookieHandlers";
import { ExpandIcon } from "../assets/icon-file";
import CustomAudioVideoControls from "./CustomAudioVideoControls";
import useHlsMedia from "../hooks/useHlsMedia";
import { parseStartSeconds } from "../ReusableFunctions/hlsSource";

const CustomVideoPlayer = ({
  videoUrl,
  handlePlay,
  handlePause,
  logRecentlyPlayed,
  modal = false,
  addEmbedCodeToMediaData,
  mediaData,
  setMediaData,
  checked = false,
  handleStartHereTime,
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seeking, setIsSeeking] = useState(false);
  const [playInline, setPlayInline] = useState(true);

  // Prefer the HLS stream URL when present; otherwise fall back to the direct file.
  const streamUrl = videoUrl.streamUrl;
  useHlsMedia(videoRef, streamUrl, parseStartSeconds(videoUrl.urlTimestamp));
  const [showExpand, setShowExpand] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement !== null);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    // Duration is tied to a useEffect so that the media and duration are available when the component mounts
    const videoElement = videoRef.current;
    const handleLoadedMetadata = () => {
      setDuration(videoElement.duration);
    };
    const handleBeforeUnload = () => {
      // Handles cookies when navigating away from the site or refreshing the page
      if (!modal) {
        videoElement.currentTime > 0
          ? setCookie(videoUrl.url, videoElement.currentTime)
          : removeCookie(videoUrl.url);
      }
    };
    function attachListeners() {
      videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
      window.addEventListener("beforeunload", handleBeforeUnload);
    }
    if (!videoElement) {
      // If no video yet, wait for it to appear in the DOM
      const intervalId = setInterval(() => {
        if (videoElement) {
          clearInterval(intervalId);
          attachListeners();
        }
      }, 100);
    } else {
      attachListeners();
    }
    return () => {
      // Handles cookies when navigating to a new page in the site
      handleBeforeUnload();
      videoElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!playInline) {
      async function expandScreen() {
        // The play command initiates the fullscreen mode. Capture currentTime to avoid changing where the video is at when the screen expands.
        const timeStamp = currentTime;
        if (!videoRef.current.paused) {
          videoRef.current.pause();
          videoRef.current.currentTime = timeStamp;
          videoRef.current.play();
          setPlayInline(true);
        } else {
          await videoRef.current.play();
          videoRef.current.pause();
          videoRef.current.currentTime = timeStamp;
          setPlayInline(true);
        }
      }
      expandScreen();
    }
  }, [playInline]);

  const handlePlayInline = () => {
    const videoElement = videoRef.current;
    if (!videoElement.fullscreenElement) {
      if (videoElement.requestFullscreen) {
        // Chrome, Firefox, Safari
        videoElement.requestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
      } else if (videoElement.msRequestFullscreen) {
        // IE11
        videoElement.msRequestFullscreen();
      } else {
        // IPhone requires a work around when using customized controls
        setPlayInline(false);
      }
    }
  };

  const handleExpand = () => {
    setShowExpand(true);
    setTimeout(() => {
      setShowExpand(false);
    }, 3000);
  };

  const handleTimeUpdate = () => {
    setCurrentTime(videoRef.current.currentTime);
    if (modal) {
      handleStartHereTime(videoRef.current.currentTime);
    }
  };

  return (
    <div className={`${modal ? "w-[328px] lg:w-[384px]" : "relative"}`}>
      <video
        ref={videoRef}
        src={
          streamUrl
            ? undefined // useHlsMedia attaches the stream to the element
            : videoUrl.urlTimestamp
            ? videoUrl.urlTimestamp
            : videoUrl.url
        }
        controls={isFullscreen ?? true}
        id="videoPlayer"
        playsInline={playInline}
        preload="metadata"
        onTimeUpdate={() => {
          // Updating while dragging is handled in the handleSeeking function
          if (!seeking) {
            handleTimeUpdate();
          }
        }}
        onPlaying={() => setIsPlaying(true)}
        onPause={() => {
          setIsPlaying(false);
          if (!modal && currentTime > 0) {
            setCookie(videoUrl.url, currentTime);
          }
        }}
        onMouseEnter={() => {
          if (!modal) {
            handleExpand();
          }
        }}
        onClick={() => {
          if (!modal) {
            handleExpand();
          }
        }}
        className={`w-full aspect-video bg-[#030303] ${
          modal ? "" : "rounded-t-xl"
        }`}
      />
      {showExpand && (
        <div
          onClick={(event) => handlePlayInline(event)}
          className="h-8 w-8 absolute top-2 left-2 h-4 w-4"
        >
          <ExpandIcon />
        </div>
      )}
      <div
        className={`flex items-center bg-primaryDark text-white ${
          modal
            ? "lg:rounded-bl-2xl min-h-[90px] lg:min-h-[140px] gap-4 lg:gap-7 flex-col px-3 p-2 lg:p-4"
            : "xs:flex-col xs:w-full xs:min-h-[140px] sm:min-h-max sm:flex-row rounded-b-xl gap-7 p-4"
        } mt-[-1px]`}
      >
        <CustomAudioVideoControls
          mediaRef={videoRef}
          mediaUrl={videoUrl.url}
          modal={modal}
          isPlaying={isPlaying}
          currentTime={currentTime}
          setCurrentTime={setCurrentTime}
          duration={duration}
          handlePlay={handlePlay}
          logRecentlyPlayed={logRecentlyPlayed}
          handlePause={handlePause}
          seeking={seeking}
          setIsSeeking={setIsSeeking}
          handleStartHereTime={handleStartHereTime}
          addEmbedCodeToMediaData={addEmbedCodeToMediaData}
          setMediaData={setMediaData}
          mediaData={mediaData}
          checked={checked}
        />
      </div>
    </div>
  );
};

export default CustomVideoPlayer;
